import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { IntegrationFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { CustomIntegrationFormContainerProps } from "models/propTypes/integrationsPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormikProps } from "formik/dist/types";
//#endregion
//#region components
import CustomIntegrationForm from "./customIntegrationForm";
import { useSettingsService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { AppCategory } from "models/enums/apps";
import { FormFieldConstants } from "utils/constants";
import { Skeletons } from "components/common";

const CustomIntegrationFormContainer: ForwardRefRenderFunction<FormHandles, CustomIntegrationFormContainerProps> = (
  props,
  ref
) => {
  const settingsService = useSettingsService();
  const [apiKey, setApiKey] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(true);
  const [selectedCompany] = useSelector((state: AppState) => [state.auth.selectedCompany]);
  const appData = props.appData as WintSuperkollAPIModel.CustomApp;

  const validationSchema = Yup.object<IntegrationFormTypes.CustomIntegration>({
    userName: Yup.string(),
    apiKey: Yup.string().when(
      FormFieldConstants.Integration.CustomIntegration.IsActive,
      ([isActive], schema: Yup.StringSchema) =>
        isActive ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
    isActive: Yup.boolean(),
    name: Yup.string().required(TranslationKeys.Validations.Required),
    description: Yup.string().required(TranslationKeys.Validations.Required),
  });

  const defaultValues: IntegrationFormTypes.CustomIntegration = {
    userName: selectedCompany.id.toString(),
    name: props.name,
    description: props.description,
    isActive: props.isActive ?? false,
    apiKey: (props.appData as WintSuperkollAPIModel.CustomApp)?.aPIKey ?? apiKey,
  };

  const onApiKeyRefresh = (name: string, updatedValue: any, formikProps: FormikProps<any>) => {
    const { values, setFieldValue } = formikProps;
    setFieldValue(FormFieldConstants.Integration.CustomIntegration.ApiKey, values.refresh);
  };

  const handleSubmit = async (values: IntegrationFormTypes.CustomIntegration) => {
    const customApp = {
      name: values.name,
      description: values.description,
      isActive: values.isActive,
      aPIKey: values.isActive ? values.apiKey : null,
    } as WintSuperkollAPIModel.CustomAppPost;

    const submitResult = {
      name: values.name,
      description: values.description,
      isActive: values.isActive,
      appCategory: AppCategory.Default,
      appData: { ...appData, aPIKey: values.isActive ? values.apiKey : null } as WintSuperkollAPIModel.CustomApp,
    };
    if (appData?.id) {
      await settingsService.updateCustomApp(appData.id, customApp);
    } else {
      const id = await settingsService.createCustomApp(customApp);
      submitResult.appData.id = id;
    }
    return props.submitResultFormatter && props.submitResultFormatter(submitResult);
  };

  React.useEffect(() => {
    props.onFormValidationChange && props.onFormValidationChange(false);
    if (appData?.aPIKey) {
      setLoading(false);
      props.onFormValidationChange && props.onFormValidationChange(true);
    } else {
      settingsService.generateCustomAppApiKey().then((res) => {
        setApiKey(res);
        setLoading(false);
        props.onFormValidationChange && props.onFormValidationChange(true);
      });
    }
  }, []);

  return loading ? (
    <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
  ) : (
    <CustomIntegrationForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
      onApiKeyRefresh={onApiKeyRefresh}
    />
  );
};

export default forwardRef(CustomIntegrationFormContainer);
